import { useMemo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import MetricCard from './components/MetricCard';
import PlatformBadge from './components/PlatformBadge';
import { MKT_ANALYTICS_DESIGN as d } from './MktAnalytics.design';

const COLORS = {
  instagram: '#E1306C',
  facebook: '#1877F2',
  google_ads: '#FBBC04',
  violet: '#7C3AED',
};

function formatNumber(num) {
  if (num == null) return '—';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString('pt-BR');
}

export default function OverviewTab({ data }) {
  if (!data)
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-400">Carregando...</p>
      </div>
    );

  const { totals, daily_comparison, top_posts } = data;

  const dailyReachData = useMemo(() => {
    if (!daily_comparison) return [];
    const igDays = daily_comparison.instagram || [];
    const fbDays = daily_comparison.facebook || [];

    const dateMap = {};
    igDays.forEach((d) => {
      dateMap[d.date] = { ...dateMap[d.date], date: d.date, instagram: d.reach };
    });
    fbDays.forEach((d) => {
      dateMap[d.date] = { ...dateMap[d.date], date: d.date, facebook: d.reach };
    });

    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [daily_comparison]);

  const dailyEngagementData = useMemo(() => {
    if (!daily_comparison) return [];
    const igDays = daily_comparison.instagram || [];
    const fbDays = daily_comparison.facebook || [];

    const dateMap = {};
    igDays.forEach((d) => {
      dateMap[d.date] = { ...dateMap[d.date], date: d.date, instagram: d.engagement };
    });
    fbDays.forEach((d) => {
      dateMap[d.date] = { ...dateMap[d.date], date: d.date, facebook: d.engagement };
    });

    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [daily_comparison]);

  const formatDateTick = (value) => {
    if (!value) return '';
    const parts = value.split('-');
    if (parts.length >= 3) return `${parts[2]}/${parts[1]}`;
    return value;
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className={d.stats.grid}>
        <MetricCard
          title="Total Seguidores"
          value={formatNumber(totals?.followers)}
          growth={totals?.followers_growth}
          icon="eva:people-outline"
          color="violet"
        />
        <MetricCard
          title="Alcance Total"
          value={formatNumber(totals?.total_reach)}
          icon="eva:eye-outline"
          color="blue"
        />
        <MetricCard
          title="Impressões Totais"
          value={formatNumber(totals?.total_impressions)}
          icon="eva:bar-chart-outline"
          color="amber"
        />
        <MetricCard
          title="Engajamento Total"
          value={formatNumber(totals?.total_engagement)}
          icon="eva:heart-outline"
          color="rose"
        />
      </div>

      {/* Charts Row */}
      <div className={d.charts.grid2}>
        {/* Daily Reach Chart */}
        <div className={d.charts.container}>
          <h3 className={d.charts.title}>Alcance Diário por Plataforma</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyReachData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name) => [formatNumber(value), name === 'instagram' ? 'Instagram' : 'Facebook']}
                labelFormatter={formatDateTick}
              />
              <Legend
                formatter={(value) => (value === 'instagram' ? 'Instagram' : 'Facebook')}
              />
              <Line
                type="monotone"
                dataKey="instagram"
                stroke={COLORS.instagram}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="facebook"
                stroke={COLORS.facebook}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Daily Engagement Chart */}
        <div className={d.charts.container}>
          <h3 className={d.charts.title}>Engajamento Diário por Plataforma</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyEngagementData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={formatDateTick} tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={formatNumber} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value, name) => [formatNumber(value), name === 'instagram' ? 'Instagram' : 'Facebook']}
                labelFormatter={formatDateTick}
              />
              <Legend
                formatter={(value) => (value === 'instagram' ? 'Instagram' : 'Facebook')}
              />
              <Bar dataKey="instagram" fill={COLORS.instagram} radius={[4, 4, 0, 0]} />
              <Bar dataKey="facebook" fill={COLORS.facebook} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Posts Table */}
      <div className={d.table.container}>
        <h3 className={d.charts.title}>Top Posts</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={d.table.header}>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Post
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Plataforma
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Curtidas
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Comentários
                </th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Alcance
                </th>
              </tr>
            </thead>
            <tbody>
              {(top_posts || []).map((post, idx) => (
                <tr key={post.external_id || idx} className={d.table.row}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      {post.thumbnail ? (
                        <img
                          src={post.thumbnail}
                          alt=""
                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-gray-400 text-xs">—</span>
                        </div>
                      )}
                      <p className="text-sm text-gray-700 line-clamp-2 max-w-xs">
                        {post.caption || 'Sem legenda'}
                      </p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <PlatformBadge platform={post.platform} />
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                    {formatNumber(post.likes)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                    {formatNumber(post.comments)}
                  </td>
                  <td className="py-3 px-4 text-right text-sm font-medium text-gray-900">
                    {formatNumber(post.reach)}
                  </td>
                </tr>
              ))}
              {(!top_posts || top_posts.length === 0) && (
                <tr>
                  <td colSpan={5} className="py-8 text-center text-sm text-gray-400">
                    Nenhum post encontrado
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
